import React, { useState, useEffect, useCallback } from 'react';
import { Pencil, Trash2, BarChart3, Eye, MousePointerClick, Plus, X } from 'lucide-react';
import api, { adminAdvertisementApi } from '../services/api'; // Issue #985: Use type-safe API wrappers
import { confirmAsync } from '../utils/toast';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling
import type { Advertisement, AdvertisementCreate, AdvertisementUpdate } from '../types'; // Issue #985: Use shared types

interface AdvertisementAnalytics {
  id: number;
  title: string;
  ad_type: string;
  is_active: boolean;
  impressions: number;
  clicks: number;
  ctr: number;
  priority: number;
  created_at: string | null;
  start_date: string | null;
  end_date: string | null;
}

interface FormData {
  title: string;
  description: string;
  ad_type: 'youtube' | 'etsy' | 'direct' | 'custom';
  target_url: string;
  image_url: string;
  thumbnail_url: string;
  display_text: string;
  is_active: boolean;
  priority: number;
  start_date: string;
  end_date: string;
}

export default function AdvertisementManagement() {
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [analytics, setAnalytics] = useState<AdvertisementAnalytics[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    ad_type: 'direct',
    target_url: '',
    image_url: '',
    thumbnail_url: '',
    display_text: 'Learn More',
    is_active: true,
    priority: 0,
    start_date: '',
    end_date: '',
  });

  const fetchAdvertisements = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Issue #985: Use type-safe adminAdvertisementApi wrapper
      const response = await adminAdvertisementApi.getAdvertisements({
        include_inactive: includeInactive,
        page_size: 200,
      });

      setAdvertisements(response.items || []);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to fetch advertisements:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchAdvertisements();
  }, [fetchAdvertisements]);

  const fetchAnalytics = async () => {
    try {
      setError(null);
      const response = await api.get<{ items: AdvertisementAnalytics[]; total: number }>(
        '/admin/advertisements/analytics/all'
      );

      setAnalytics(response.data.items || []);
      setShowAnalytics(true);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to fetch analytics:', apiError);
      setError(apiError.message);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      // Issue #985: Use type-safe adminAdvertisementApi wrappers
      // Preserve "clear field" semantics: empty strings -> null (not undefined)
      if (editingId) {
        // Update existing advertisement
        const updateData: AdvertisementUpdate = {
          title: formData.title,
          description: formData.description || null,
          ad_type: formData.ad_type,
          target_url: formData.target_url,
          image_url: formData.image_url || null,
          thumbnail_url: formData.thumbnail_url || null,
          display_text: formData.display_text || null,
          is_active: formData.is_active,
          priority: formData.priority,
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
        };
        await adminAdvertisementApi.updateAdvertisement(editingId, updateData);
        setSuccess('Advertisement updated successfully');
      } else {
        // Create new advertisement
        const createData: AdvertisementCreate = {
          title: formData.title,
          description: formData.description || null,
          ad_type: formData.ad_type,
          target_url: formData.target_url,
          image_url: formData.image_url || null,
          thumbnail_url: formData.thumbnail_url || null,
          display_text: formData.display_text || null,
          is_active: formData.is_active,
          priority: formData.priority,
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
        };
        await adminAdvertisementApi.createAdvertisement(createData);
        setSuccess('Advertisement created successfully');
      }

      await fetchAdvertisements();
      resetForm();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    }
  };

  const handleEdit = (ad: Advertisement) => {
    setEditingId(ad.id);
    setFormData({
      title: ad.title,
      description: ad.description || '',
      ad_type: ad.ad_type,
      target_url: ad.target_url,
      image_url: ad.image_url || '',
      thumbnail_url: ad.thumbnail_url || '',
      display_text: ad.display_text || 'Learn More',
      is_active: ad.is_active,
      priority: ad.priority,
      start_date: ad.start_date ? ad.start_date.split('T')[0] : '',
      end_date: ad.end_date ? ad.end_date.split('T')[0] : '',
    });
    setShowForm(true);
    setShowAnalytics(false);
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirmAsync('Are you sure you want to delete this advertisement?');
    if (!confirmed) return;

    try {
      setError(null);
      // Issue #985: Use type-safe adminAdvertisementApi wrapper
      await adminAdvertisementApi.deleteAdvertisement(id);
      setSuccess('Advertisement deleted successfully');
      await fetchAdvertisements();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to delete advertisement:', apiError);
      setError(apiError.message);
    }
  };

  const resetFormData = () => {
    setFormData({
      title: '',
      description: '',
      ad_type: 'direct',
      target_url: '',
      image_url: '',
      thumbnail_url: '',
      display_text: 'Learn More',
      is_active: true,
      priority: 0,
      start_date: '',
      end_date: '',
    });
    setEditingId(null);
  };

  const resetForm = () => {
    resetFormData();
    setShowForm(false);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Advertisement Management</h2>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setShowAnalytics(false);
              setShowForm(!showForm);
              if (!showForm) resetFormData();
            }}
            className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            {showForm ? <X className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
            <span>{showForm ? 'Cancel' : 'New Advertisement'}</span>
          </button>
          <button
            onClick={() => {
              setShowForm(false);
              if (showAnalytics) {
                setShowAnalytics(false);
              } else {
                fetchAnalytics();
              }
            }}
            className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <BarChart3 className="h-5 w-5" />
            <span>{showAnalytics ? 'Hide Analytics' : 'View Analytics'}</span>
          </button>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-teal-50 border border-teal-200 text-primary-dark px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">
            {editingId ? 'Edit Advertisement' : 'Create New Advertisement'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Advertisement title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ad Type *
                </label>
                <select
                  required
                  value={formData.ad_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      ad_type: e.target.value as 'youtube' | 'etsy' | 'direct' | 'custom',
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="youtube">📺 YouTube</option>
                  <option value="etsy">🛍️ Etsy</option>
                  <option value="direct">🔗 Direct Link</option>
                  <option value="custom">⭐ Custom</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  rows={3}
                  placeholder="Advertisement description"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target URL *
                </label>
                <input
                  type="url"
                  required
                  value={formData.target_url}
                  onChange={(e) => setFormData({ ...formData, target_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image URL
                </label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Thumbnail URL
                </label>
                <input
                  type="url"
                  value={formData.thumbnail_url}
                  onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="https://example.com/thumb.jpg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Button Text
                </label>
                <input
                  type="text"
                  value={formData.display_text}
                  onChange={(e) => setFormData({ ...formData, display_text: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Learn More"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                  Active
                </label>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="bg-primary hover:bg-primary-dark text-white px-6 py-2 rounded-lg"
              >
                {editingId ? 'Update' : 'Create'} Advertisement
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Analytics View */}
      {showAnalytics && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Advertisement Analytics</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-4">Title</th>
                  <th className="text-left py-2 px-4">Type</th>
                  <th className="text-center py-2 px-4">Status</th>
                  <th className="text-right py-2 px-4">Impressions</th>
                  <th className="text-right py-2 px-4">Clicks</th>
                  <th className="text-right py-2 px-4">CTR</th>
                  <th className="text-center py-2 px-4">Priority</th>
                </tr>
              </thead>
              <tbody>
                {analytics.map((ad) => (
                  <tr key={ad.id} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-4">{ad.title}</td>
                    <td className="py-2 px-4 capitalize">{ad.ad_type}</td>
                    <td className="py-2 px-4 text-center">
                      {ad.is_active ? (
                        <span className="text-secondary font-semibold">Active</span>
                      ) : (
                        <span className="text-gray-400">Inactive</span>
                      )}
                    </td>
                    <td className="py-2 px-4 text-right flex items-center justify-end gap-1">
                      <Eye className="h-4 w-4 text-gray-400" />
                      {ad.impressions.toLocaleString()}
                    </td>
                    <td className="py-2 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <MousePointerClick className="h-4 w-4 text-gray-400" />
                        {ad.clicks.toLocaleString()}
                      </div>
                    </td>
                    <td className="py-2 px-4 text-right font-semibold">{ad.ctr.toFixed(2)}%</td>
                    <td className="py-2 px-4 text-center">{ad.priority}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Advertisements List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">
            Advertisements ({advertisements.length})
          </h3>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="includeInactive"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="includeInactive" className="text-sm text-gray-700">
              Show inactive
            </label>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : advertisements.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No advertisements found. Create one to get started!
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-4">Title</th>
                  <th className="text-left py-2 px-4">Type</th>
                  <th className="text-center py-2 px-4">Status</th>
                  <th className="text-center py-2 px-4">Priority</th>
                  <th className="text-left py-2 px-4">Dates</th>
                  <th className="text-right py-2 px-4">Stats</th>
                  <th className="text-right py-2 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {advertisements.map((ad) => (
                  <tr key={ad.id} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-4 font-medium">{ad.title}</td>
                    <td className="py-2 px-4 capitalize">{ad.ad_type}</td>
                    <td className="py-2 px-4 text-center">
                      {ad.is_active ? (
                        <span className="inline-block bg-teal-100 text-primary-dark text-xs px-2 py-1 rounded">
                          Active
                        </span>
                      ) : (
                        <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-4 text-center">{ad.priority}</td>
                    <td className="py-2 px-4 text-sm text-gray-600">
                      {ad.start_date ? formatDate(ad.start_date) : 'No start'} -{' '}
                      {ad.end_date ? formatDate(ad.end_date) : 'No end'}
                    </td>
                    <td className="py-2 px-4 text-right text-sm text-gray-600">
                      {ad.impressions} views / {ad.clicks} clicks
                    </td>
                    <td className="py-2 px-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEdit(ad)}
                          className="text-secondary hover:text-ink"
                          title="Edit advertisement"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(ad.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete advertisement"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
